// Court rental pricing — client mirror.
//
// AUTHORITY LIVES IN functions/rentalPricing.js. This copy exists so the
// booking dialog can show what a court will cost before the round-trip; it
// decides nothing. CRA can't import from functions/, so the two must be
// changed together — the same constraint memberGuards.js documents.

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toMins = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + (m || 0);
};

export const isWeekendDate = (date) => {
  const d = new Date(`${date}T12:00:00`).getDay();
  return d === 0 || d === 6;
};

/** Weekend beats prime; a blank prime/weekend figure falls back to non-prime. */
export function hourlyFor(rate, { weekend, prime }) {
  if (weekend && Number(rate?.weekendRate) > 0) return Number(rate.weekendRate);
  if (!weekend && prime && Number(rate?.primeRate) > 0) return Number(rate.primeRate);
  return Number(rate?.hourlyRate) || 0;
}

/**
 * The card as it stands on `date`. A card may carry one scheduledChange,
 * { from: 'YYYY-MM-DD', hourlyRate?, primeRate?, weekendRate? }: on and after
 * `from`, each figure it sets replaces the card's own, and the ones it leaves
 * out stay as they are. Priced by the date played, not the date booked, so a
 * January court booked in December pays January's rate.
 */
export function rateOn(rate, date) {
  const ch = rate && rate.scheduledChange;
  if (!ch || !ch.from || !date || date < ch.from) return rate;
  const next = { ...rate };
  for (const k of ['hourlyRate', 'primeRate', 'weekendRate']) {
    if (ch[k] != null && ch[k] !== '' && Number.isFinite(Number(ch[k]))) next[k] = Number(ch[k]);
  }
  return next;
}

/**
 * The renter-facing line announcing a card's scheduled change, while it is
 * still ahead (today before `from`); '' otherwise. Shown when booking and on
 * the Stripe payment page.
 */
export function rateChangeNotice(rate, today) {
  const ch = rate && rate.scheduledChange;
  if (!ch || !ch.from || !today || today >= ch.from) return '';
  const when = new Date(`${ch.from}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const money = (v) => `$${Number(v) % 1 ? Number(v).toFixed(2) : Number(v)}`;
  const parts = [['hourlyRate', 'daytime'], ['primeRate', 'prime time'], ['weekendRate', 'weekends']]
    .filter(([k]) => ch[k] != null && Number(ch[k]) !== Number(rate[k]));
  if (!parts.length) return '';
  // Group the figures that are the same, so "$20 … daytime and weekends".
  const byAmount = {};
  parts.forEach(([k, l]) => { (byAmount[ch[k]] = byAmount[ch[k]] || []).push(l); });
  const text = Object.entries(byAmount)
    .map(([amt, ls]) => `${money(amt)} per court per hour for ${ls.join(' and ')}`).join('; ');
  return `Court rates change on ${when}: ${text}. Courts played from then are charged the new rate.`;
}

/** Cost of [startTime, endTime) on `date`, split at prime boundaries. */
export function priceRental(startTime, endTime, date, courtCount, rate, primeBlocks) {
  const startMins = toMins(startTime);
  const endMins = toMins(endTime);
  const courts = Math.max(1, Number(courtCount) || 1);
  if (!(endMins > startMins) || !rate) return { amount: 0, breakdown: [] };
  rate = rateOn(rate, date);

  const weekend = isWeekendDate(date);
  const dayAbbr = DAY_ABBR[new Date(`${date}T12:00:00`).getDay()];

  const primeMins = new Set();
  for (const tb of primeBlocks || []) {
    if (!tb.isPrime || !Array.isArray(tb.days) || !tb.days.includes(dayAbbr)) continue;
    const bs = Math.max(toMins(tb.startTime), startMins);
    const be = Math.min(toMins(tb.endTime), endMins);
    for (let m = bs; m < be; m++) primeMins.add(m);
  }

  const breakdown = [];
  let segStart = startMins;
  let segPrime = primeMins.has(startMins);
  for (let m = startMins + 1; m <= endMins; m++) {
    const p = m < endMins ? primeMins.has(m) : null;
    if (p !== segPrime) {
      const hours = (m - segStart) / 60;
      const hourly = hourlyFor(rate, { weekend, prime: segPrime });
      breakdown.push({
        kind: weekend ? 'weekend' : (segPrime ? 'prime' : 'regular'),
        hours, hourly, cost: Math.round(hourly * hours * courts * 100) / 100,
      });
      segStart = m;
      segPrime = p;
    }
  }
  const amount = Math.round(breakdown.reduce((s, b) => s + b.cost, 0) * 100) / 100;
  return { amount, breakdown };
}
